import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Learning Stage Constants
export const LEARNING_STAGES = {
  MIDDLE_SCHOOL: 'middle_school',
  HIGH_SCHOOL: 'high_school',
  POST_HS: 'post_hs'
};

// Feature access defaults per stage
const DEFAULT_FEATURE_ACCESS = {
  middle_school: {
    show_financial_tools: false,
    show_college_costs: false,
    show_scholarship_modeling: false,
    show_path_lock: false,
    show_college_comparison: false,
    show_application_tracker: false,
    show_career_earnings: false,
    show_resume_ats: false,
    enable_missions: true,
    enable_career_cards: true,
    enable_dream_board: true,
    max_choices_per_screen: 1,
    max_visible_tabs: 6,
    reading_level: 'ms_simple'
  },
  high_school: {
    show_financial_tools: true,
    show_college_costs: true,
    show_scholarship_modeling: true,
    show_path_lock: true,
    show_college_comparison: true,
    show_application_tracker: true,
    show_career_earnings: true,
    show_resume_ats: false,
    enable_missions: false,
    enable_career_cards: true,
    enable_dream_board: false,
    max_choices_per_screen: 5,
    max_visible_tabs: 10,
    reading_level: 'standard'
  },
  post_hs: {
    show_financial_tools: true,
    show_college_costs: true,
    show_scholarship_modeling: true,
    show_path_lock: true,
    show_college_comparison: true,
    show_application_tracker: true,
    show_career_earnings: true,
    show_resume_ats: true,
    enable_missions: false,
    enable_career_cards: false,
    enable_dream_board: false,
    max_choices_per_screen: 10,
    max_visible_tabs: 15,
    reading_level: 'advanced'
  }
};

const LearningStageContext = createContext();

export const useLearningStage = () => {
  const context = useContext(LearningStageContext);
  if (!context) {
    throw new Error('useLearningStage must be used within a LearningStageProvider');
  }
  return context;
};

export const LearningStageProvider = ({ children }) => {
  const { user, getAuthHeader, isAuthenticated } = useAuth();
  const [learningStage, setLearningStage] = useState(null);
  const [featurePack, setFeaturePack] = useState(null);
  const [navTree, setNavTree] = useState(null);
  const [homeRoute, setHomeRoute] = useState('/app/dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [grade, setGrade] = useState(null);

  // Load learning stage config
  const loadConfig = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/learning-stage/config`, getAuthHeader());
      
      setLearningStage(response.data.learning_stage);
      setFeaturePack(response.data.feature_pack);
      setNavTree(response.data.nav_tree);
      setHomeRoute(response.data.home_route);
      setGrade(response.data.grade);
    } catch (error) {
      console.error('Failed to load learning stage config:', error);
      setLearningStage(LEARNING_STAGES.HIGH_SCHOOL);
      setFeaturePack(DEFAULT_FEATURE_ACCESS.high_school);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, user]);

  // Set learning stage (during onboarding)
  const setLearningStageFromGrade = useCallback(async (gradeValue, age = null, guardianEmail = null) => {
    try {
      const response = await axios.post(
        `${API}/learning-stage/set`,
        { grade: gradeValue, age, guardian_email: guardianEmail },
        getAuthHeader()
      );
      
      setLearningStage(response.data.learning_stage);
      setFeaturePack(response.data.feature_pack);
      setNavTree(response.data.nav_tree);
      setHomeRoute(response.data.home_route);
      setGrade(gradeValue);
      
      return response.data;
    } catch (error) {
      console.error('Failed to set learning stage:', error);
      throw error;
    }
  }, [getAuthHeader]);

  // Update grade (progression)
  const updateGrade = useCallback(async (newGrade) => {
    try {
      const response = await axios.post(
        `${API}/learning-stage/update-grade?grade=${newGrade}`,
        {},
        getAuthHeader()
      );
      
      if (response.data.stage_changed) {
        setLearningStage(response.data.learning_stage);
        setFeaturePack(response.data.feature_pack);
        setHomeRoute(response.data.home_route);
        // Reload nav tree
        await loadConfig();
      }
      
      setGrade(newGrade);
      return response.data;
    } catch (error) {
      console.error('Failed to update grade:', error);
      throw error;
    }
  }, [getAuthHeader, loadConfig]);

  // Check if a feature is accessible
  const hasFeatureAccess = useCallback((featureName) => {
    if (!featurePack) {
      // Default based on stage
      const defaults = DEFAULT_FEATURE_ACCESS[learningStage] || DEFAULT_FEATURE_ACCESS.high_school;
      return defaults[featureName] ?? false;
    }
    return featurePack[featureName] ?? false;
  }, [featurePack, learningStage]);

  // Check if user is in middle school
  const isMiddleSchool = learningStage === LEARNING_STAGES.MIDDLE_SCHOOL;
  const isHighSchool = learningStage === LEARNING_STAGES.HIGH_SCHOOL;
  const isPostHS = learningStage === LEARNING_STAGES.POST_HS;

  // Get appropriate route prefix
  const getRoutePrefix = useCallback(() => {
    if (isMiddleSchool) return '/ms';
    if (isHighSchool) return '/hs';
    return '/app';
  }, [isMiddleSchool, isHighSchool]);

  // Get reading level for AI responses
  const getReadingLevel = useCallback(() => {
    return featurePack?.reading_level || 'standard';
  }, [featurePack]);

  const value = {
    learningStage,
    featurePack,
    navTree,
    homeRoute,
    grade,
    isLoading,
    isMiddleSchool,
    isHighSchool,
    isPostHS,
    setLearningStageFromGrade,
    updateGrade,
    hasFeatureAccess,
    getRoutePrefix,
    getReadingLevel,
    loadConfig
  };

  return (
    <LearningStageContext.Provider value={value}>
      {children}
    </LearningStageContext.Provider>
  );
};

export default LearningStageContext;
